// Author-provided narrative tabs referenced from the PR description:
//   <!-- changelist:tab title="Brief" src="https://example.com/a/x/" -->
// `display="link"` shows a link card instead of embedding the page, for
// sites that refuse to be framed. Only a trusted author's tabs are ever
// embedded; everyone else's are link cards.
import type { AuthorAssociation } from '../api/queries';
import type { OrgPolicy } from '../api/server';

/** Whether a pull request's author may have their brief tabs embedded. */
export type AuthorTrust = 'trusted' | 'untrusted';

const TRUSTED: ReadonlySet<string> = new Set<AuthorAssociation>(['OWNER', 'MEMBER', 'COLLABORATOR']);

/**
 * OWNER, MEMBER and COLLABORATOR are trusted. Every other association,
 * including a missing or unrecognized one, is untrusted.
 */
export function authorTrust(association: AuthorAssociation | null | undefined): AuthorTrust {
  return typeof association === 'string' && TRUSTED.has(association) ? 'trusted' : 'untrusted';
}

/** Why an untrusted author's tab is a link card. */
export const UNTRUSTED_REASON = "Shown as a link because the author isn't a collaborator on this repository.";

export interface BriefTab {
  title: string;
  src: string;
  /** `embed` (default): sandboxed iframe. `link`: a card that opens `src`. */
  display: 'embed' | 'link';
}

export interface ResolvedTab extends BriefTab {
  /** Origin of `src` (shown above the frame), or null when unparsable. */
  origin: string | null;
  allowed: boolean;
  /** Why the tab is blocked, or shown only as a link although it asked to be embedded. */
  reason: string | null;
}

const TAG = /<!--\s*changelist:tab\b([\s\S]*?)-->/g;
const ATTR = /([a-zA-Z_-]+)\s*=\s*(?:"([^"]*)"|'([^']*)')/g;

function decodeEntities(s: string): string {
  return s
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');
}

export function parseBriefTabs(body: string | null | undefined): BriefTab[] {
  if (!body) return [];
  const tabs: BriefTab[] = [];
  for (const m of body.matchAll(TAG)) {
    const attrs: Record<string, string> = {};
    for (const a of m[1].matchAll(ATTR)) attrs[a[1].toLowerCase()] = decodeEntities(a[2] ?? a[3] ?? '');
    const src = attrs.src?.trim();
    if (!src) continue;
    tabs.push({
      title: attrs.title?.trim() || 'Brief',
      src,
      display: attrs.display?.trim().toLowerCase() === 'link' ? 'link' : 'embed',
    });
  }
  return tabs;
}

function isLocalhost(url: URL): boolean {
  return url.protocol === 'http:' && (url.hostname === 'localhost' || url.hostname === '127.0.0.1');
}

/**
 * Decide how a tab is shown. Only https: sources (plus http://localhost:*
 * in dev) are shown at all. A policy that is loading or could not be read
 * embeds nothing: the tab becomes a link card. A ready policy with an empty
 * `brief_origins` allows any such origin; otherwise the origin must be
 * listed. An allowed tab from an untrusted author is always a link card.
 */
export function resolveTab(tab: BriefTab, policy: OrgPolicy, dev: boolean, trust: AuthorTrust): ResolvedTab {
  let url: URL;
  try {
    url = new URL(tab.src);
  } catch {
    return { ...tab, origin: null, allowed: false, reason: 'The source is not a valid URL.' };
  }
  const origin = url.origin;
  const secure = url.protocol === 'https:' || (dev && isLocalhost(url));
  if (!secure) return { ...tab, origin, allowed: false, reason: 'Only https: sources can be shown.' };
  if (policy.status !== 'ready') {
    const reason =
      policy.status === 'loading'
        ? "Waiting for the organization's embed policy."
        : policy.status === 'pending'
          ? "The organization's policy hasn't been resolved yet."
          : "The organization's embed policy could not be read.";
    return { ...tab, display: 'link', origin, allowed: true, reason };
  }
  const briefOrigins = policy.settings.brief_origins;
  const listed =
    briefOrigins.length === 0 ||
    briefOrigins.some((o) => {
      try {
        return new URL(o).origin === origin;
      } catch {
        return o.replace(/\/+$/, '') === origin;
      }
    });
  if (!listed) return { ...tab, origin, allowed: false, reason: `${origin} is not in this organization's allowed brief origins.` };
  if (trust === 'untrusted') return { ...tab, display: 'link', origin, allowed: true, reason: UNTRUSTED_REASON };
  return { ...tab, origin, allowed: true, reason: null };
}

/**
 * The tab the overview opens on: the first brief for a trusted author,
 * otherwise the description (-1).
 */
export function defaultTab(trust: AuthorTrust, tabs: readonly ResolvedTab[]): number {
  return trust === 'trusted' && tabs.length > 0 ? 0 : -1;
}

/**
 * The sandbox for an embedded brief served from `origin`. Scripts, popups
 * and forms only; `allow-same-origin` is added for a foreign origin and
 * never for `appOrigin`, where it would let the frame script this app.
 */
export function briefSandbox(origin: string | null, appOrigin: string): string {
  const base = 'allow-scripts allow-popups allow-forms';
  return origin && origin !== appOrigin ? base + ' allow-same-origin' : base;
}

/**
 * Whether a message from a brief frame may be acted on: only from a tab that
 * is allowed and embedded, sent from its own origin or an opaque (`null`) one.
 * A link tab never accepts messages, whether or not a frame exists for it.
 */
export function acceptsFrameMessage(tab: ResolvedTab | undefined, messageOrigin: string): boolean {
  if (!tab || !tab.allowed || tab.display !== 'embed') return false;
  return messageOrigin === tab.origin || messageOrigin === 'null';
}

export interface OpenMessage {
  path: string;
  line: number | null;
}

/** Validate a `{type:"changelist:open", path, line}` message from a brief frame. */
export function parseOpenMessage(data: unknown, files: ReadonlySet<string>): OpenMessage | null {
  if (!data || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  if (d.type !== 'changelist:open' || typeof d.path !== 'string') return null;
  const path = d.path.replace(/^\/+/, '');
  if (!files.has(path)) return null;
  const line = typeof d.line === 'number' && Number.isInteger(d.line) && d.line > 0 ? d.line : null;
  return { path, line };
}
